// Browser Compatibility Module for Browser APIs
// This file provides utility functions for browser APIs with varying support

/**
 * Safe localStorage access with try-catch for better browser compatibility
 * Handles cases where localStorage might be unavailable (private browsing, disabled, etc.)
 * 
 * @param key The key to retrieve from localStorage
 * @returns The stored value or null if not found or if localStorage is unavailable
 */
export function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.warn(`Error accessing localStorage for key ${key}:`, error);
    return null;
  }
}

/**
 * Safe localStorage write with try-catch for better browser compatibility
 * Handles cases where localStorage might be unavailable (private browsing, disabled, etc.)
 * 
 * @param key The key to store in localStorage
 * @param value The value to store
 * @returns Boolean indicating success
 */
export function safeSetItem(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.warn(`Error setting localStorage for key ${key}:`, error);
    return false;
  }
}

/**
 * Safe sessionStorage access with try-catch for better browser compatibility
 * 
 * @param key The key to retrieve from sessionStorage
 * @returns The stored value or null if not found or if sessionStorage is unavailable
 */
export function safeSessionGetItem(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch (error) {
    console.warn(`Error accessing sessionStorage for key ${key}:`, error);
    return null;
  }
}

/**
 * Safe sessionStorage write with try-catch for better browser compatibility
 * 
 * @param key The key to store in sessionStorage
 * @param value The value to store
 * @returns Boolean indicating success
 */
export function safeSessionSetItem(key: string, value: string): boolean {
  try {
    sessionStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.warn(`Error setting sessionStorage for key ${key}:`, error);
    return false;
  }
}

/**
 * Check if system prefers dark mode with fallback
 * 
 * @returns Boolean indicating if the system prefers dark mode
 */
export function checkSystemPrefersDark(): boolean {
  try {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch (error) {
    console.warn('Error checking system color scheme preference:', error);
    return false; // Default to light if can't detect
  }
}

/**
 * Safe window.matchMedia wrapper with fallback
 * 
 * @param query The media query to check
 * @returns MediaQueryList if supported, or a fallback object
 */
export function safeMatchMedia(query: string): MediaQueryList | { matches: boolean, addEventListener: () => void, removeEventListener: () => void } {
  try {
    if (window.matchMedia) {
      return window.matchMedia(query);
    }
  } catch (error) {
    console.warn(`Error using matchMedia with query ${query}:`, error);
  }
  
  // Return a fallback object if matchMedia is not supported
  return {
    matches: false,
    addEventListener: () => {},
    removeEventListener: () => {},
  };
}

/**
 * TODO: Browser Compatibility - Browser APIs
 * 
 * The following browser APIs may have varying levels of support across browsers:
 * 
 * 1. localStorage/sessionStorage: Use the safe* functions provided in this module
 *    for consistent behavior across browsers and private browsing modes
 * 
 * 2. matchMedia: Use the safeMatchMedia function for better compatibility
 * 
 * 3. Intersection Observer: Consider using a polyfill for older browsers
 *    or provide fallback behavior
 * 
 * 4. requestAnimationFrame: Consider using a polyfill for older browsers
 *    or provide setTimeout fallbacks
 */